import { editsOf, findSection, hasValue, value, values } from '../../parser/fortigate'
import { finding, type Finding, type Rule } from '../types'
import { policies } from './policy'

/**
 * Who can reach what: administrator privilege, traffic between trusted segments,
 * and how much of the record survives.
 */

/** Log severities that discard everything below them, including the useful part. */
const TOO_COARSE = ['emergency', 'alert', 'critical', 'error']

export const adminWithoutTwoFactor: Rule = {
  id: 'fg-admin-no-2fa',
  title: 'Administrator without two-factor authentication',
  run(config) {
    const admins = editsOf(config, 'system', 'admin')
    if (admins.length === 0) return []

    const single = admins.filter((a) => {
      const factor = value(a, 'two-factor')
      return factor === undefined || factor === 'disable'
    })
    if (single.length === 0) return []

    return [
      finding(adminWithoutTwoFactor, {
        severity: 'high',
        title: `${single.length} of ${admins.length} administrator accounts rely on a password alone`,
        detail:
          `Accounts ${single.map((a) => `"${a.key}"`).join(', ')} have no second factor. A single ` +
          'reused or phished password is then enough to take full control of the device that ' +
          'enforces the network perimeter.',
        location: 'system admin',
        line: single[0]?.line,
        pci: '8.4.1',
        remediation:
          `config system admin\n    edit "${single[0]?.key ?? '<account>'}"\n` +
          '        set two-factor fortitoken\n        set fortitoken "<serial>"\n    next\nend',
      }),
    ]
  },
}

export const tooManySuperAdmins: Rule = {
  id: 'fg-excessive-super-admins',
  title: 'More super-admin accounts than necessary',
  run(config) {
    const supers = editsOf(config, 'system', 'admin').filter(
      (a) => value(a, 'accprofile') === 'super_admin',
    )
    if (supers.length <= 2) return []

    return [
      finding(tooManySuperAdmins, {
        severity: 'medium',
        title: `${supers.length} accounts hold full super-admin rights`,
        detail:
          `Accounts ${supers.map((a) => `"${a.key}"`).join(', ')} all have the super_admin profile. ` +
          'Every one of them can disable logging, change any policy and create further accounts, so ' +
          'each is an equally valuable target and none of their actions can be constrained.',
        location: 'system admin',
        line: supers[0]?.line,
        // No PCI citation: the least-privilege sub-number is unverified.
        remediation:
          'config system accprofile\n    edit "netops-readonly"\n        set sysgrp read\n' +
          '        set fwgrp read\n    next\nend\n' +
          '# Then move day-to-day accounts onto the narrower profile.',
      }),
    ]
  },
}

export const zoneAllowsIntrazone: Rule = {
  id: 'fg-zone-intrazone-allow',
  title: 'Zone permits traffic between its own interfaces unfiltered',
  run(config) {
    const zones = editsOf(config, 'system', 'zone').filter(
      (z) => value(z, 'intrazone') === 'allow',
    )
    if (zones.length === 0) return []

    return zones.map((zone) =>
      finding(zoneAllowsIntrazone, {
        severity: 'medium',
        title: `Zone "${zone.key}" allows unfiltered traffic between its members`,
        detail:
          `Zone ${zone.key} has intrazone set to allow, covering interfaces ` +
          `${values(zone, 'interface').join(', ') || '(unnamed)'}. Traffic between those interfaces ` +
          'bypasses the policy table entirely — it is not matched, not filtered and not logged, so ' +
          'a compromised host on one of them can reach the others freely.',
        location: `system zone ${zone.key}`,
        line: zone.line,
        pci: '1.2.1',
        remediation:
          `config system zone\n    edit "${zone.key}"\n        set intrazone deny\n    next\nend\n` +
          '# Then write explicit policies for the flows that are genuinely needed.',
      }),
    )
  },
}

export const dhcpServerOnWan: Rule = {
  id: 'fg-dhcp-on-wan',
  title: 'DHCP server running on a WAN interface',
  run(config) {
    const wan = new Set(
      editsOf(config, 'system', 'interface')
        .filter((i) => value(i, 'role') === 'wan')
        .map((i) => i.key.toLowerCase()),
    )
    if (wan.size === 0) return []

    const offenders = editsOf(config, 'system', 'dhcp', 'server').filter((server) => {
      if (value(server, 'status') === 'disable') return false
      const iface = value(server, 'interface')
      return iface !== undefined && wan.has(iface)
    })
    if (offenders.length === 0) return []

    return offenders.map((server) =>
      finding(dhcpServerOnWan, {
        severity: 'high',
        title: `DHCP server ${server.key} is bound to a WAN interface`,
        detail:
          `system dhcp server ${server.key} serves addresses on ${value(server, 'interface')}, which ` +
          'is marked as a WAN interface. The device is handing out addressing to the untrusted side, ' +
          'which is either a misconfiguration or an interface whose role is wrong — both worth fixing.',
        location: `system dhcp server ${server.key}`,
        line: server.line,
        remediation:
          `config system dhcp server\n    edit ${server.key}\n        set status disable\n    next\nend`,
      }),
    )
  },
}

export const coarseLogSeverity: Rule = {
  id: 'fg-log-severity-coarse',
  title: 'Log severity threshold discards routine events',
  run(config) {
    const out: Finding[] = []
    const targets: Array<[string[], string]> = [
      [['log', 'disk', 'setting'], 'local disk'],
      [['log', 'memory', 'setting'], 'memory'],
      [['log', 'syslogd', 'filter'], 'syslog'],
      [['log', 'fortianalyzer', 'filter'], 'FortiAnalyzer'],
    ]

    for (const [path, label] of targets) {
      const section = findSection(config, ...path)
      if (section === undefined) continue
      const severity = value(section, 'severity')
      if (severity === undefined || !TOO_COARSE.includes(severity)) continue

      out.push(
        finding(coarseLogSeverity, {
          severity: 'medium',
          title: `${label} logging only records "${severity}" and above`,
          detail:
            `${path.join(' ')} sets severity to ${severity}, so notice, information and warning ` +
            'events are discarded. Those are the levels that carry successful logins, policy hits ' +
            'and configuration changes — the material an investigation actually needs.',
          location: path.join(' '),
          line: section.line,
          pci: '10.2.1',
          remediation: `config ${path.join(' ')}\n    set severity information\nend`,
        }),
      )
    }

    return out
  },
}

export const shallowSslInspection: Rule = {
  id: 'fg-ssl-inspection-shallow',
  title: 'Perimeter policy inspects only the certificate, not the traffic',
  run(config) {
    const wan = new Set(
      editsOf(config, 'system', 'interface')
        .filter((i) => value(i, 'role') === 'wan')
        .map((i) => i.key.toLowerCase()),
    )
    if (wan.size === 0) return []

    const raw = editsOf(config, 'firewall', 'policy')
    const out: Finding[] = []

    for (const [index, policy] of policies(config).entries()) {
      if (!policy.enabled || policy.action !== 'accept') continue
      if (!policy.dstintf.some((i) => wan.has(i))) continue

      const entry = raw[index]
      const profile = value(entry, 'ssl-ssh-profile')
      if (profile !== 'certificate-inspection') continue
      // Only meaningful when some content inspection is actually attached.
      const inspects = ['av-profile', 'ips-sensor', 'webfilter-profile'].some((key) => {
        const v = entry?.sets[key]?.join(' ').trim()
        return v !== undefined && v !== ''
      })
      if (!inspects) continue

      out.push(
        finding(shallowSslInspection, {
          severity: 'low',
          title: `Policy ${policy.key} inspects content but only reads TLS certificates`,
          detail:
            `Policy ${policy.key} attaches content inspection while using certificate-inspection, ` +
            'which sees only the certificate and SNI. Since almost all outbound traffic is encrypted, ' +
            'the antivirus and IPS profiles on this policy examine very little of it.',
          location: `firewall policy ${policy.key}`,
          line: policy.line,
          remediation:
            `config firewall policy\n    edit ${policy.key}\n` +
            '        set ssl-ssh-profile "deep-inspection"\n    next\nend\n' +
            '# Deep inspection requires the CA certificate on every client and has privacy implications.',
        }),
      )
    }

    return out
  },
}

export const managementProtocolBreadth: Rule = {
  id: 'fg-mgmt-protocol-breadth',
  title: 'Interface answers an unusually broad set of management protocols',
  run(config) {
    const out: Finding[] = []
    const managementish = ['https', 'ssh', 'snmp', 'http', 'telnet', 'fgfm', 'radius-acct', 'ftm']

    for (const iface of editsOf(config, 'system', 'interface')) {
      const enabled = managementish.filter((p) => hasValue(iface, 'allowaccess', p))
      if (enabled.length < 4) continue

      out.push(
        finding(managementProtocolBreadth, {
          severity: 'low',
          title: `Interface ${iface.key} answers ${enabled.length} management protocols`,
          detail:
            `allowaccess on ${iface.key} includes ${enabled.join(', ')}. Each protocol is a separate ` +
            'listening service and a separate advisory to track; most deployments need at most two.',
          location: `system interface ${iface.key}`,
          line: iface.line,
          pci: '1.2.1',
          remediation:
            `config system interface\n    edit "${iface.key}"\n` +
            '        set allowaccess ping https\n    next\nend',
        }),
      )
    }

    return out
  },
}

export const ACCESS_RULES: Rule[] = [
  adminWithoutTwoFactor,
  tooManySuperAdmins,
  zoneAllowsIntrazone,
  dhcpServerOnWan,
  coarseLogSeverity,
  shallowSslInspection,
  managementProtocolBreadth,
]
